import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../../../utils/api';
import { Spinner } from 'react-bootstrap';
import { useToast } from '../../../components/ui/ToastProvider';
import safeStorage from '../../../utils/safeStorage';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';

export default function AssetAdminPage() {
  const { show: showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  
  // User limit management state
  const userLimitCardRef = useRef(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [limitValue, setLimitValue] = useState('');
  const [savingLimit, setSavingLimit] = useState(false);
  const [limitError, setLimitError] = useState(null);
  const [limitSuccess, setLimitSuccess] = useState(false);

  // Report table state
  const [sortField, setSortField] = useState('total_size_bytes');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterTerm, setFilterTerm] = useState('');

  // Global config state
  const [globalLimit, setGlobalLimit] = useState('');
  const [globalConfigLoading, setGlobalConfigLoading] = useState(true);
  const [globalConfigError, setGlobalConfigError] = useState(null);
  const [savingGlobalLimit, setSavingGlobalLimit] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(null);
  const [guardrailResult, setGuardrailResult] = useState(null);
  const [guardrailLoading, setGuardrailLoading] = useState(false);

  // Load report on mount
  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setError(null);
    try {
      const res = await api.get('/assets/admin/report/');
      setReport(res?.data || null);
    } catch (e) {
      setError(e?.response?.status === 403 ? 'Not authorized' : 'Failed to load report');
    } finally {
      setReportLoading(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);

  const loadGlobalConfig = useCallback(async () => {
    setGlobalConfigLoading(true);
    setGlobalConfigError(null);
    try {
      const res = await api.get('/assets/admin/config/');
      const limit = res?.data?.default_monthly_upload_limit;
      setGlobalLimit(limit !== undefined && limit !== null ? String(limit) : '');
    } catch (e) {
      setGlobalConfigError(e?.response?.status === 403 ? 'Not authorized' : 'Failed to load global configuration');
    } finally {
      setGlobalConfigLoading(false);
    }
  }, []);

  useEffect(() => { loadGlobalConfig(); }, [loadGlobalConfig]);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const res = await api.get('/assets/admin/metrics/');
      setMetrics(res?.data || null);
    } catch (e) {
      setMetricsError(e?.response?.status === 403 ? 'Not authorized' : 'Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const runGuardrailCheck = useCallback(async () => {
    setGuardrailLoading(true);
    setGuardrailResult(null);
    try {
      const res = await api.post('/assets/admin/guardrails/run/', { days: 2 });
      const data = res?.data || {};
      setGuardrailResult(data);
      if (data.alerts && data.alerts.length > 0 && !(data.alerts.length === 1 && data.alerts[0].includes('No AssetDailyMetrics'))) {
        showToast({ title: 'Guardrail alerts', body: data.alerts.join(' | '), variant: 'danger' });
      } else {
        showToast({ title: 'Guardrails OK', body: 'No alerts triggered.', variant: 'success' });
      }
    } catch (e) {
      setGuardrailResult({ alerts: [`Failed to run guardrail check: ${e?.response?.data?.error || e.message || 'Unknown error'}`] });
      showToast({ title: 'Guardrail failed', body: 'Unable to run guardrail check.', variant: 'danger' });
    } finally {
      setGuardrailLoading(false);
    }
  }, [showToast]);

  const metricsChartData = useMemo(() => {
    if (!metrics?.days?.length) return [];
    return [...metrics.days].reverse().map((day) => ({
      date: day.date.slice(5),
      init: day.init_count,
      finalize: day.finalize_count,
      storageGb: day.total_finalize_size_bytes ? day.total_finalize_size_bytes / (1024 * 1024 * 1024) : 0,
      avgListMs: day.avg_asset_list_duration_ms || 0,
    }));
  }, [metrics]);

  // Handle user selection
  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
    setLimitValue(user ? String(user.limit ?? '') : '');
    setLimitError(null);
    setLimitSuccess(false);
    if (user && userLimitCardRef.current) {
      try {
        userLimitCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) {}
    }
  }, []);

  // Save user limit
  const handleSaveLimit = useCallback(async () => {
    if (!selectedUser || !limitValue) {
      setLimitError('Please select a user and enter a limit');
      return;
    }
    
    const limit = parseInt(limitValue, 10);
    if (isNaN(limit) || limit < 1) {
      setLimitError('Limit must be a number greater than 0');
      return;
    }

    setSavingLimit(true);
    setLimitError(null);
    setLimitSuccess(false);
    
    try {
      await api.post('/assets/admin/set_upload_limit/', {
        user_id: selectedUser.id,
        limit: limit,
      });
      setLimitSuccess(true);
      // Reload report to get updated limits
      await loadReport();
      // Update selected user's limit in local state
      setSelectedUser({ ...selectedUser, limit });
    } catch (e) {
      setLimitError(e?.response?.data?.error || 'Failed to save limit');
    } finally {
      setSavingLimit(false);
    }
  }, [selectedUser, limitValue, loadReport]);

  // Export CSV
  const handleExportCSV = useCallback(async () => {
    try {
      const token = safeStorage.getItem('token');
      const baseURL = api.defaults.baseURL || 'http://127.0.0.1:8000/api';
      const url = `${baseURL}/assets/admin/report/?output=csv`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'assets_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      try {
        showToast({ title: 'Download ready', body: 'CSV successfully downloaded.', variant: 'success' });
      } catch (_) {}
    } catch (e) {
      console.error('CSV export error:', e);
      setError(`Failed to export CSV: ${e.message || 'Unknown error'}`);
      try {
        showToast({ title: 'Download failed', body: 'Unable to export CSV. Please try again.', variant: 'danger' });
      } catch (_) {}
    }
  }, [showToast]);

  const handleSaveGlobalLimit = useCallback(async () => {
    if (!globalLimit) {
      setGlobalConfigError('Please enter a limit.');
      return;
    }
    const parsed = parseInt(globalLimit, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      setGlobalConfigError('Limit must be a number greater than 0.');
      return;
    }
    setSavingGlobalLimit(true);
    setGlobalConfigError(null);
    try {
      await api.post('/assets/admin/config/', { default_monthly_upload_limit: parsed });
      showToast({ title: 'Global limit updated', body: `Default monthly limit set to ${parsed}.`, variant: 'success' });
      await loadGlobalConfig();
      await loadReport();
    } catch (e) {
      const message = e?.response?.data?.error || 'Failed to update global limit';
      setGlobalConfigError(message);
      showToast({ title: 'Update failed', body: message, variant: 'danger' });
    } finally {
      setSavingGlobalLimit(false);
    }
  }, [globalLimit, loadGlobalConfig, loadReport, showToast]);

  // Sort and filter report data
  const sortedAndFilteredUsers = useMemo(() => {
    if (!report?.users) return [];
    
    let filtered = report.users;
    
    // Apply filter
    if (filterTerm) {
      const term = filterTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.username?.toLowerCase().includes(term) ||
        String(u.user_id).includes(term)
      );
    }
    
    // Apply sort
    filtered = [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle null/undefined
      if (aVal == null) aVal = 0;
      if (bVal == null) bVal = 0;
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    return filtered;
  }, [report, sortField, sortDirection, filterTerm]);

  // Format bytes
  const formatBytes = (bytes) => {
    if (bytes == null || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Handle table header click for sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) return <div className="p-3">Loading…</div>;
  if (error && !report) return <div className="p-3 text-danger">{error}</div>;

  return (
    <div className="container py-3" data-testid="asset-admin-page">
      <h4 className="mb-3">Asset Management</h4>

      {/* Metrics Section */}
      <div className="mb-4">
        <div className="p-3 border rounded-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Metrics & Guardrails</h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={loadMetrics}
                disabled={metricsLoading}
              >
                {metricsLoading ? 'Refreshing…' : 'Refresh metrics'}
              </button>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={runGuardrailCheck}
                disabled={guardrailLoading}
              >
                {guardrailLoading ? 'Running…' : 'Run guardrail check'}
              </button>
            </div>
          </div>
          {metricsError && <div className="alert alert-danger">{metricsError}</div>}
          {metricsLoading && !metrics && <div>Loading metrics…</div>}
          {!metricsLoading && metrics?.days?.length > 0 && (() => {
            const latest = metrics.days[0];
            const formatValue = (v, suffix = '') => (v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`);
            const formatBytes = (bytes) => {
              if (bytes === null || bytes === undefined) return '—';
              const k = 1024;
              const sizes = ['B', 'KB', 'MB', 'GB'];
              const i = Math.floor(Math.log(bytes) / Math.log(k));
              return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
            };
            return (
              <>
                <div className="text-muted small mb-2">Date: {latest.date}</div>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Init / Finalize</div>
                      <div className="fw-semibold">{latest.init_count} / {latest.finalize_count}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Avg object size</div>
                      <div className="fw-semibold">{formatBytes(latest.avg_finalize_size_bytes)}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">429 count</div>
                      <div className="fw-semibold">{latest.limit_429_count}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">CDN hit rate</div>
                      <div className="fw-semibold">
                        {latest.cdn_cache_hit_rate == null ? '—' : `${Math.round(latest.cdn_cache_hit_rate * 100)}%`}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Avg asset list ms</div>
                      <div className="fw-semibold">{formatValue(latest.avg_asset_list_duration_ms)}</div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="border rounded p-2">
                      <div className="text-muted small">Avg URL gen ms</div>
                      <div className="fw-semibold">{formatValue(latest.avg_url_generation_duration_ms)}</div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
          {guardrailResult?.alerts && (
            <div className={`alert alert-${guardrailResult.alerts.length > 0 ? 'warning' : 'success'} mt-3`}>
              {guardrailResult.alerts.length === 0 || (guardrailResult.alerts.length === 1 && guardrailResult.alerts[0].includes('No AssetDailyMetrics'))
                ? 'No guardrail alerts triggered.'
                : (
                  <ul className="mb-0">
                    {guardrailResult.alerts.map((msg, idx) => (
                      <li key={idx}>{msg}</li>
                    ))}
                  </ul>
                )}
            </div>
          )}
          {metricsChartData.length > 0 && (
            <div className="row g-3 mt-3">
              <div className="col-md-6">
                <div className="border rounded p-2" style={{ height: 240 }}>
                  <div className="text-muted small mb-1">Init vs Finalize (14 days)</div>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={metricsChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="init" stroke="#0d6efd" name="Init" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="finalize" stroke="#198754" name="Finalize" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-md-6">
                <div className="border rounded p-2" style={{ height: 240 }}>
                  <div className="text-muted small mb-1">Daily storage added (GB)</div>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={metricsChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="storageGb" stroke="#dc3545" name="Storage GB" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
          {!metricsLoading && metrics?.days?.length > 0 && (
            <div className="mt-3">
              <div className="text-muted small mb-1">Last 14 days</div>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Init</th>
                      <th>Finalize</th>
                      <th>429</th>
                      <th>Avg size</th>
                      <th>Storage GB</th>
                      <th>Asset list ms</th>
                      <th>URL ms</th>
                      <th>CDN hit %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.days.map((day) => (
                      <tr key={day.date}>
                        <td>{day.date}</td>
                        <td>{day.init_count}</td>
                        <td>{day.finalize_count}</td>
                        <td>{day.limit_429_count}</td>
                        <td>{day.avg_finalize_size_bytes ? `${(day.avg_finalize_size_bytes / 1024).toFixed(1)} KB` : '—'}</td>
                        <td>{day.total_finalize_size_bytes ? (day.total_finalize_size_bytes / (1024 * 1024 * 1024)).toFixed(3) : '—'}</td>
                        <td>{day.avg_asset_list_duration_ms ? Math.round(day.avg_asset_list_duration_ms) : '—'}</td>
                        <td>{day.avg_url_generation_duration_ms ? Math.round(day.avg_url_generation_duration_ms) : '—'}</td>
                        <td>{day.cdn_cache_hit_rate == null ? '—' : `${Math.round(day.cdn_cache_hit_rate * 100)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Limit Section */}
      <div className="mb-4">
        <div className="p-3 border rounded-3">
          <h5 className="mb-3">Global Upload Limit</h5>
          {globalConfigError && (
            <div className="alert alert-danger">{globalConfigError}</div>
          )}
          <div className="mb-3">
            <label className="form-label fw-semibold">Default Monthly Upload Limit</label>
            <input
              type="number"
              className="form-control"
              min="1"
              value={globalLimit}
              onChange={(e) => setGlobalLimit(e.target.value)}
              disabled={globalConfigLoading || savingGlobalLimit}
            />
            <div className="form-text">Applies to all users who do not have a custom limit.</div>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveGlobalLimit}
            disabled={globalConfigLoading || savingGlobalLimit || !globalLimit}
          >
            {savingGlobalLimit ? 'Saving…' : 'Save Global Limit'}
          </button>
        </div>
      </div>
      
      {/* User Limit Management Section */}
      <div className="mb-4" ref={userLimitCardRef}>
        <div className="p-3 border rounded-3">
          <h5 className="mb-3">Set User Upload Limit</h5>
          <p className="text-muted small mb-3">
            Filter the Usage Report below and click <strong>Set limit</strong> on a user. The user will appear here for editing.
          </p>

          {/* Selected User Display */}
          {selectedUser ? (
            <div className="mb-3 p-2 bg-light rounded">
              <div className="fw-semibold">{selectedUser.username}</div>
              <div className="text-muted small">User ID: {selectedUser.id}</div>
              <div className="text-muted small">Current limit: {selectedUser.limit ?? `Global default (${globalLimit || 'n/a'})`}</div>
            </div>
          ) : (
            <div className="alert alert-light small mb-3">
              No user selected. Use the table to choose someone to override.
            </div>
          )}

          {/* Limit Input */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Monthly Upload Limit</label>
            <input
              type="number"
              className="form-control"
              placeholder="Enter limit (e.g., 25)"
              value={limitValue}
              onChange={(e) => {
                setLimitValue(e.target.value);
                setLimitError(null);
                setLimitSuccess(false);
              }}
              min="1"
              disabled={!selectedUser || savingLimit}
            />
            <div className="form-text">Number of assets the user can upload per month</div>
          </div>

          {/* Error/Success Messages */}
          {limitError && (
            <div className="alert alert-danger mb-3">{limitError}</div>
          )}
          {limitSuccess && (
            <div className="alert alert-success mb-3">Limit updated successfully!</div>
          )}

          {/* Save Button */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveLimit}
            disabled={!selectedUser || !limitValue || savingLimit}
          >
            {savingLimit ? 'Saving…' : 'Save Limit'}
          </button>
        </div>
      </div>

      {/* Usage Report Section */}
      <div className="mb-4">
        <div className="p-3 border rounded-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Usage Report</h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={loadReport}
                disabled={reportLoading}
              >
                {reportLoading ? 'Refreshing…' : 'Refresh'}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleExportCSV}
                disabled={!report}
              >
                Export CSV
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          {report?.summary && (
            <div className="mb-3 p-2 bg-light rounded">
              <div className="row text-center">
                <div className="col-md-6">
                  <div className="fw-semibold">Total Objects</div>
                  <div className="fs-4">{report.summary.total_objects.toLocaleString()}</div>
                </div>
                <div className="col-md-6">
                  <div className="fw-semibold">Total Size</div>
                  <div className="fs-4">{formatBytes(report.summary.total_size_bytes)}</div>
                </div>
              </div>
            </div>
          )}

          {/* Filter */}
          <div className="mb-3">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filter by username or user ID..."
              value={filterTerm}
              onChange={(e) => setFilterTerm(e.target.value)}
            />
          </div>

          {/* Report Table */}
          {reportLoading ? (
            <div className="text-center p-4">
              <Spinner />
              <div className="mt-2">Loading report…</div>
            </div>
          ) : sortedAndFilteredUsers.length === 0 ? (
            <div className="text-muted text-center p-4">No users found</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('user_id')}
                    >
                      User ID<SortIcon field="user_id" />
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('username')}
                    >
                      Username<SortIcon field="username" />
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('total_objects')}
                      className="text-end"
                    >
                      Objects<SortIcon field="total_objects" />
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('total_size_bytes')}
                      className="text-end"
                    >
                      Size<SortIcon field="total_size_bytes" />
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('last_30_days_uploads')}
                      className="text-end"
                    >
                      Last 30 Days<SortIcon field="last_30_days_uploads" />
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('limit')}
                      className="text-end"
                    >
                      Limit<SortIcon field="limit" />
                    </th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndFilteredUsers.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.user_id}</td>
                      <td>{user.username}</td>
                      <td className="text-end">{user.total_objects.toLocaleString()}</td>
                      <td className="text-end">{formatBytes(user.total_size_bytes)}</td>
                      <td className="text-end">{user.last_30_days_uploads}</td>
                      <td className="text-end">{user.limit}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleUserSelect({
                            id: user.user_id,
                            username: user.username,
                            limit: user.limit,
                          })}
                        >
                          Set limit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filterTerm && sortedAndFilteredUsers.length > 0 && (
            <div className="text-muted small mt-2">
              Showing {sortedAndFilteredUsers.length} of {report?.users?.length || 0} users
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

